import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Month-grid calendar. Items appear on the cell matching their date column.
// Defaults to system_key="due_date" if present, else the first date column.
// Days outside the current month are dimmed but still rendered to fill the
// 6×7 grid.
//
// Phase-2.5 scope: read-only positioning + click-to-open. Drag-to-reschedule
// is a follow-up (similar HTML5 DnD pattern as kanban).
export default function CalendarView({ items, board, peopleIndex, kindIndex, onCardClick }) {
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [colId, setColId] = useState(null);

  const dateCols = (board?.columns || []).filter((c) => c.type === "date");
  const chosenCol =
    (colId && dateCols.find((c) => c._id === colId)) ||
    dateCols.find((c) => c.system_key === "due_date") ||
    dateCols[0] ||
    null;

  const itemsByDate = useMemo(() => {
    const map = new Map();
    if (!chosenCol) return map;
    for (const it of items) {
      const raw = it.column_values?.[chosenCol._id]?.value;
      if (!raw) continue;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    return map;
  }, [items, chosenCol]);

  if (!chosenCol) {
    return (
      <div className="kanban-empty-board">
        Calendar needs at least one <strong>date column</strong>. Add one (e.g. "Due date") to your board.
      </div>
    );
  }

  const monthLabel = anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const firstDay = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const startGrid = new Date(firstDay);
  startGrid.setDate(firstDay.getDate() - firstDay.getDay()); // back up to Sunday
  const days = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(startGrid);
    d.setDate(startGrid.getDate() + i);
    return d;
  });

  const today = new Date();
  const isToday = (d) =>
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  const inMonth = (d) => d.getMonth() === anchor.getMonth();

  const prev = () => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() - 1, 1));
  const next = () => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + 1, 1));
  const todayBtn = () => setAnchor(new Date(today.getFullYear(), today.getMonth(), 1));

  return (
    <div className="calendar-view">
      <div className="cal-toolbar">
        <button className="toolbar-btn" onClick={prev} title="Previous"><ChevronLeft size={16} /></button>
        <button className="toolbar-btn" onClick={todayBtn}>Today</button>
        <button className="toolbar-btn" onClick={next} title="Next"><ChevronRight size={16} /></button>
        <span className="cal-month">{monthLabel}</span>
        <span className="cal-spacer" />
        <span className="kanban-label">Date column:</span>
        <select className="kanban-select" value={chosenCol._id} onChange={(e) => setColId(e.target.value)}>
          {dateCols.map((c) => (
            <option key={c._id} value={c._id}>{c.title}</option>
          ))}
        </select>
      </div>

      <div className="cal-grid">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="cal-dow">{d}</div>
        ))}
        {days.map((d, i) => {
          const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          const cellItems = itemsByDate.get(key) || [];
          return (
            <div
              key={i}
              className={`cal-cell ${inMonth(d) ? "" : "outside"} ${isToday(d) ? "today" : ""}`}
            >
              <div className="cal-date">{d.getDate()}</div>
              <div className="cal-items">
                {cellItems.slice(0, 4).map((it) => {
                  const kindDef = kindIndex[it.kind];
                  return (
                    <button
                      key={it._id}
                      className="cal-item"
                      style={{ borderLeftColor: kindDef?.color || "#ef3a3a" }}
                      onClick={() => onCardClick(it)}
                      title={it.title}
                    >
                      {it.title}
                    </button>
                  );
                })}
                {cellItems.length > 4 && (
                  <span className="cal-more">+{cellItems.length - 4} more</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
